/**
 * Mock providers — 20 approved providers across the major categories, plus
 * the dev-session provider (so the dev login lands on a populated dashboard).
 *
 * Each provider has stable per-category junction rows so search/category
 * filtering works against the mock dataset.
 */

import type { Tables } from '@/lib/db.types'
import { providerId, providerUserId, DEV_USER_IDS } from './ids'
import { categoryId } from './ids'
import { PROVIDER_USER_PROFILES } from './profiles'

type Provider = Tables<'providers'>
type ProviderCategory = Tables<'provider_categories'>

const NOW = new Date('2026-05-01T08:00:00.000Z').toISOString()
const T = (daysAgo: number) =>
  new Date(Date.parse(NOW) - daysAgo * 86400_000).toISOString()

interface ProviderSeed {
  business_name: string
  city: string
  postal_code: string
  description_sv: string
  description_en: string
  hourly_rate: number
  avg_rating: number
  rating_count: number
  response_time_minutes: number
  completed_jobs: number
  coverage_radius_km: number
  category_slugs: string[]
  bankid_verified: boolean
  insurance_verified: boolean
  f_skatt: boolean
  website: string | null
  org_number: string
}

const SEEDS: ProviderSeed[] = [
  {
    business_name: 'Flyttkungen AB',
    city: 'Stockholm', postal_code: '11432',
    description_sv: 'Vi flyttar dig snabbt och tryggt i hela Stockholmsområdet. Över 15 år i branschen.',
    description_en: 'Fast, safe moving across Greater Stockholm. 15+ years in business.',
    hourly_rate: 690, avg_rating: 4.8, rating_count: 142, response_time_minutes: 38, completed_jobs: 312,
    coverage_radius_km: 50, category_slugs: ['flytt', 'bohagsflytt', 'kontorsflytt', 'mobelmontering'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: 'https://flyttkungen.se', org_number: '559012-1111',
  },
  {
    business_name: 'Städexperten Stockholm',
    city: 'Stockholm', postal_code: '11643',
    description_sv: 'Hemstädning, flyttstädning och fönsterputs. RUT-avdrag direkt på fakturan.',
    description_en: 'Home, move-out and window cleaning. RUT deduction on invoice.',
    hourly_rate: 420, avg_rating: 4.9, rating_count: 287, response_time_minutes: 22, completed_jobs: 540,
    coverage_radius_km: 30, category_slugs: ['stad', 'hemstadning', 'flyttstadning', 'fonsterputs', 'storstadning'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: 'https://stadexperten.se', org_number: '559012-2222',
  },
  {
    business_name: 'Elektriker Stockholm AB',
    city: 'Stockholm', postal_code: '11355',
    description_sv: 'Behöriga elektriker för installation, felsökning och inspektion. Akut hjälp dygnet runt.',
    description_en: 'Licensed electricians for installation, troubleshooting and inspection. 24/7 emergency service.',
    hourly_rate: 895, avg_rating: 4.7, rating_count: 96, response_time_minutes: 45, completed_jobs: 188,
    coverage_radius_km: 40, category_slugs: ['el-vvs', 'elektriker'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: 'https://elek-stockholm.se', org_number: '559012-3333',
  },
  {
    business_name: 'VVS-teamet i Stockholm',
    city: 'Stockholm', postal_code: '11824',
    description_sv: 'Allt inom VVS — från läckande kran till komplett badrumsrenovering.',
    description_en: 'Full-service plumbing — from a leaky tap to a full bathroom renovation.',
    hourly_rate: 750, avg_rating: 4.6, rating_count: 73, response_time_minutes: 55, completed_jobs: 132,
    coverage_radius_km: 35, category_slugs: ['el-vvs', 'vvs', 'installation-vitvaror'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: null, org_number: '559012-4444',
  },
  {
    business_name: 'Snickeri & Bygg AB',
    city: 'Stockholm', postal_code: '11250',
    description_sv: 'Erfarna snickare för kök, inredning och tillbyggnader. ROT-avdrag direkt.',
    description_en: 'Experienced carpenters for kitchens, interiors and extensions. ROT deduction.',
    hourly_rate: 650, avg_rating: 4.9, rating_count: 118, response_time_minutes: 95, completed_jobs: 207,
    coverage_radius_km: 60, category_slugs: ['hantverk', 'snickare', 'mobelmontering'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: 'https://snickeriab.se', org_number: '559012-5555',
  },
  {
    business_name: 'Måleri Partners',
    city: 'Göteborg', postal_code: '41319',
    description_sv: 'Målning, tapetsering och spackel — invändigt och utvändigt. Yrkesutbildade målare.',
    description_en: 'Painting, wallpapering and plastering — indoor and outdoor. Certified painters.',
    hourly_rate: 580, avg_rating: 4.7, rating_count: 64, response_time_minutes: 70, completed_jobs: 98,
    coverage_radius_km: 45, category_slugs: ['hantverk', 'malare', 'tapetsering'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: null, org_number: '559012-6666',
  },
  {
    business_name: 'Gröna Trädgården',
    city: 'Göteborg', postal_code: '41526',
    description_sv: 'Gräsklippning, häckklippning och trädfällning. Avtalskunder välkomna.',
    description_en: 'Lawn mowing, hedge trimming and tree felling. Subscription customers welcome.',
    hourly_rate: 495, avg_rating: 4.8, rating_count: 88, response_time_minutes: 50, completed_jobs: 167,
    coverage_radius_km: 40, category_slugs: ['tradgard', 'grasklippning', 'hackklippning', 'tradfallning'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: 'https://gotradgard.se', org_number: '559012-7777',
  },
  {
    business_name: 'Hundpensionatet i Solna',
    city: 'Solna', postal_code: '17141',
    description_sv: 'Trygg dagis och övernattning för din hund. Erfaren personal med utbildning.',
    description_en: 'Safe daycare and overnight stays for your dog. Experienced, trained staff.',
    hourly_rate: 320, avg_rating: 5.0, rating_count: 53, response_time_minutes: 18, completed_jobs: 145,
    coverage_radius_km: 20, category_slugs: ['husdjur', 'hundvakt', 'dogwalker'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: 'https://hundpensionatet.se', org_number: '559012-8888',
  },
  {
    business_name: 'Datorhjälpen',
    city: 'Stockholm', postal_code: '11354',
    description_sv: 'Hembesök eller fjärrsupport för PC, Mac, skrivare och nätverk. Inget krångel.',
    description_en: 'In-home visits or remote support for PC, Mac, printers and networks. Hassle-free.',
    hourly_rate: 590, avg_rating: 4.6, rating_count: 41, response_time_minutes: 28, completed_jobs: 92,
    coverage_radius_km: 25, category_slugs: ['it', 'datohjalp', 'mobilreparation'],
    bankid_verified: true, insurance_verified: false, f_skatt: true,
    website: null, org_number: '559012-9999',
  },
  {
    business_name: 'Lärare Emma — Matte & Svenska',
    city: 'Uppsala', postal_code: '75234',
    description_sv: 'Privatlektioner i matematik och svenska, åk 6 till gymnasiet. Online eller hembesök.',
    description_en: 'Private tutoring in math and Swedish, grade 6 through high school. Online or in-home.',
    hourly_rate: 450, avg_rating: 4.9, rating_count: 31, response_time_minutes: 12, completed_jobs: 76,
    coverage_radius_km: 15, category_slugs: ['undervisning', 'matteundervisning', 'sprakundervisning'],
    bankid_verified: true, insurance_verified: false, f_skatt: false,
    website: null, org_number: '880101-1234',
  },
  {
    business_name: 'Bohagsflytt Malmö',
    city: 'Malmö', postal_code: '21134',
    description_sv: 'Bohagsflytt och magasinering i Skåne. Snabba, ärliga priser utan dolda avgifter.',
    description_en: 'Household moving and storage in Skåne. Quick, transparent pricing.',
    hourly_rate: 620, avg_rating: 4.5, rating_count: 78, response_time_minutes: 65, completed_jobs: 145,
    coverage_radius_km: 70, category_slugs: ['flytt', 'bohagsflytt', 'magasinering'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: 'https://bohagsflytt-malmo.se', org_number: '559013-1111',
  },
  {
    business_name: 'Golvhantverk Stockholm',
    city: 'Stockholm', postal_code: '11645',
    description_sv: 'Trä-, laminat-, vinyl- och klinkergolv. Slipning och nytt golv på en dag.',
    description_en: 'Wood, laminate, vinyl and tile floors. Sanding and full installs same-day.',
    hourly_rate: 720, avg_rating: 4.8, rating_count: 52, response_time_minutes: 40, completed_jobs: 89,
    coverage_radius_km: 35, category_slugs: ['hantverk', 'golvlaggare', 'kakelsattare'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: null, org_number: '559013-2222',
  },
  {
    business_name: 'Värmepump Installation Nord',
    city: 'Göteborg', postal_code: '41115',
    description_sv: 'Certifierade montörer för luft-luft, luft-vatten och bergvärmeanläggningar.',
    description_en: 'Certified installers for air-air, air-water and ground-source heat pumps.',
    hourly_rate: 825, avg_rating: 4.7, rating_count: 47, response_time_minutes: 80, completed_jobs: 71,
    coverage_radius_km: 80, category_slugs: ['el-vvs', 'varmepump', 'ventilation'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: 'https://vp-installation.se', org_number: '559013-3333',
  },
  {
    business_name: 'Härliga Trädgården',
    city: 'Lund', postal_code: '22221',
    description_sv: 'Anläggning, plantering och löpande skötsel av trädgården. RUT-avdrag.',
    description_en: 'Landscaping, planting and ongoing garden care. RUT deduction.',
    hourly_rate: 460, avg_rating: 4.9, rating_count: 39, response_time_minutes: 33, completed_jobs: 64,
    coverage_radius_km: 30, category_slugs: ['tradgard', 'anlaggning', 'grasklippning'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: null, org_number: '559013-4444',
  },
  {
    business_name: 'Berg Möblering & Montering',
    city: 'Stockholm', postal_code: '11820',
    description_sv: 'IKEA, Mio och andra — vi monterar dina möbler snabbt och tryggt.',
    description_en: 'IKEA, Mio and others — we assemble your furniture quickly and safely.',
    hourly_rate: 540, avg_rating: 4.7, rating_count: 124, response_time_minutes: 20, completed_jobs: 280,
    coverage_radius_km: 30, category_slugs: ['flytt', 'mobelmontering'],
    bankid_verified: true, insurance_verified: false, f_skatt: true,
    website: 'https://bergmoblering.se', org_number: '559013-5555',
  },
  {
    business_name: 'Trappspecialisten',
    city: 'Göteborg', postal_code: '41254',
    description_sv: 'Trappstädning för bostadsrättsföreningar och hyresfastigheter. Fasta abonnemang.',
    description_en: 'Stairwell cleaning for housing co-ops and rental properties. Fixed subscriptions.',
    hourly_rate: 380, avg_rating: 4.6, rating_count: 28, response_time_minutes: 48, completed_jobs: 156,
    coverage_radius_km: 45, category_slugs: ['stad', 'trappstadning', 'kontorsstadning'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: null, org_number: '559013-6666',
  },
  {
    business_name: 'Kakelsättaren i Stockholm',
    city: 'Stockholm', postal_code: '11240',
    description_sv: 'Kakel och klinker i badrum, kök och uterum. Allt arbete utförs enligt branschregler.',
    description_en: 'Tiles in bathrooms, kitchens and patios. All work follows industry standards.',
    hourly_rate: 695, avg_rating: 4.8, rating_count: 67, response_time_minutes: 60, completed_jobs: 113,
    coverage_radius_km: 40, category_slugs: ['hantverk', 'kakelsattare'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: null, org_number: '559013-7777',
  },
  {
    business_name: 'Språklaget',
    city: 'Stockholm', postal_code: '11530',
    description_sv: 'Engelska, spanska och tyska från nybörjare till avancerad. Online och fysiska lektioner.',
    description_en: 'English, Spanish and German from beginner to advanced. Online and in-person lessons.',
    hourly_rate: 495, avg_rating: 4.9, rating_count: 22, response_time_minutes: 15, completed_jobs: 48,
    coverage_radius_km: 10, category_slugs: ['undervisning', 'sprakundervisning'],
    bankid_verified: true, insurance_verified: false, f_skatt: true,
    website: null, org_number: '559013-8888',
  },
  {
    business_name: 'Malmö VVS Akut',
    city: 'Malmö', postal_code: '21456',
    description_sv: 'Akut VVS dygnet runt — vi är på plats inom 60 minuter i hela Skåne.',
    description_en: 'Emergency plumbing 24/7 — on site within 60 minutes across Skåne.',
    hourly_rate: 850, avg_rating: 4.4, rating_count: 56, response_time_minutes: 25, completed_jobs: 167,
    coverage_radius_km: 90, category_slugs: ['el-vvs', 'vvs'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: 'https://malmovvs.se', org_number: '559013-9999',
  },
  {
    business_name: 'Pianoflytt Stockholm',
    city: 'Stockholm', postal_code: '11340',
    description_sv: 'Specialiserade pianoflyttare med 20+ års erfarenhet. Försäkring upp till 500 000 kr.',
    description_en: 'Specialist piano movers with 20+ years experience. Insurance up to SEK 500,000.',
    hourly_rate: 1100, avg_rating: 5.0, rating_count: 38, response_time_minutes: 60, completed_jobs: 84,
    coverage_radius_km: 100, category_slugs: ['flytt', 'pianoflytt'],
    bankid_verified: true, insurance_verified: true, f_skatt: true,
    website: 'https://piano-flytt.se', org_number: '559014-1111',
  },
]

export const MOCK_PROVIDERS: Provider[] = SEEDS.map((s, i) => {
  const n = i + 1
  return {
    id: providerId(n),
    user_id: providerUserId(n),
    business_name: s.business_name,
    org_number: s.org_number,
    description_sv: s.description_sv,
    description_en: s.description_en,
    contact_email: PROVIDER_USER_PROFILES[i]?.email ?? `provider${n}@hjalptorget.dev`,
    contact_phone: PROVIDER_USER_PROFILES[i]?.phone ?? null,
    website: s.website,
    logo_url: null,
    cover_image_url: null,
    hourly_rate: s.hourly_rate,
    currency: 'SEK',
    status: 'approved' as const,
    avg_rating: s.avg_rating,
    rating_count: s.rating_count,
    response_time_minutes: s.response_time_minutes,
    completed_jobs: s.completed_jobs,
    coverage_center: null,
    coverage_radius_km: s.coverage_radius_km,
    address_line: null,
    city: s.city,
    postal_code: s.postal_code,
    f_skatt: s.f_skatt,
    insurance_verified: s.insurance_verified,
    bankid_verified: s.bankid_verified,
    stripe_account_id: `acct_mock_${n.toString().padStart(4, '0')}`,
    stripe_charges_enabled: true,
    stripe_payouts_enabled: true,
    created_at: T(200 - n * 7),
    updated_at: T(7),
  } satisfies Provider
})

// ── Dev session provider — well-populated so the provider dashboard looks alive ─
export const DEV_PROVIDER: Provider = {
  id: providerId(99),
  user_id: DEV_USER_IDS.provider,
  business_name: 'Sigges Hantverk AB',
  org_number: '559099-0001',
  description_sv:
    'Hej! Jag är Sigge och driver Sigges Hantverk AB i Stockholm. Vi tar hand om allt från ' +
    'IKEA-montering och mindre snickerijobb till hela köks- och badrumsrenoveringar. ' +
    '20+ års erfarenhet, försäkrade, ROT-avdrag direkt på fakturan.',
  description_en:
    'Hi! I run Sigge\'s Carpentry in Stockholm. From IKEA assembly to full kitchen and ' +
    'bathroom renovations. 20+ years experience, fully insured, ROT deduction on invoice.',
  contact_email: 'test-leverantor@hjalptorget.dev',
  contact_phone: '+46739000002',
  website: 'https://sigges-hantverk.se',
  logo_url: null,
  cover_image_url: null,
  hourly_rate: 720,
  currency: 'SEK',
  status: 'approved',
  avg_rating: 4.85,
  rating_count: 47,
  response_time_minutes: 18,
  completed_jobs: 142,
  coverage_center: null,
  coverage_radius_km: 35,
  address_line: 'Storgatan 1',
  city: 'Stockholm',
  postal_code: '11451',
  f_skatt: true,
  insurance_verified: true,
  bankid_verified: true,
  stripe_account_id: 'acct_mock_dev_provider',
  stripe_charges_enabled: true,
  stripe_payouts_enabled: true,
  created_at: T(420),
  updated_at: T(2),
}

// All providers visible in search/listing
export const ALL_MOCK_PROVIDERS: Provider[] = [...MOCK_PROVIDERS, DEV_PROVIDER]

// ── Provider ⇄ category junction ─────────────────────────────────────
export const MOCK_PROVIDER_CATEGORIES: ProviderCategory[] = (() => {
  const rows: ProviderCategory[] = []
  SEEDS.forEach((s, i) => {
    const pid = providerId(i + 1)
    for (const slug of s.category_slugs) {
      rows.push({
        provider_id: pid,
        category_id: categoryId(slug),
        created_at: NOW,
      })
    }
  })
  // Dev provider belongs to a handful of categories so its search hits look alive
  for (const slug of ['hantverk', 'snickare', 'mobelmontering', 'kakelsattare', 'malare']) {
    rows.push({
      provider_id: DEV_PROVIDER.id,
      category_id: categoryId(slug),
      created_at: NOW,
    })
  }
  return rows
})()

// ── Lookup helpers ────────────────────────────────────────────────────
export function findProviderById(id: string): Provider | undefined {
  return ALL_MOCK_PROVIDERS.find((p) => p.id === id)
}

export function findProviderByUserId(userId: string): Provider | undefined {
  return ALL_MOCK_PROVIDERS.find((p) => p.user_id === userId)
}

export function findProviderCategorySlugsFor(providerEntityId: string): string[] {
  const catIds = MOCK_PROVIDER_CATEGORIES
    .filter((pc) => pc.provider_id === providerEntityId)
    .map((pc) => pc.category_id)
  return catIds
}
